// Wires the vendored OfflineSync SDK to the web app: config from env, device id + company scoping, lifecycle.
import { auth } from '../api/axios';
import { createLocalStorageDatabase } from './localStorageDatabase';
import { createHttpNetworkMonitor } from './networkMonitor';
import { OfflineSync, type SyncConfig } from './sdk';

export const OFFLINESYNC_STREAMS = ['article', 'client', 'document', 'stock', 'paiement'] as const;

const DEVICE_ID_KEY = '@offlinesync/deviceId';

export function getOfflineSyncBaseUrl(): string | undefined {
  return process.env.REACT_APP_OFFLINESYNC_URL || undefined;
}

export function getOfflineSyncApiKey(): string | undefined {
  return process.env.REACT_APP_OFFLINESYNC_API_KEY || undefined;
}

/** Inert until both env vars are set, so builds without a sync engine behave exactly as before. */
export function isOfflineSyncConfigured(): boolean {
  return Boolean(getOfflineSyncBaseUrl() && getOfflineSyncApiKey());
}

function getDeviceId(): string {
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = `tfweb-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `tfweb-${Math.random().toString(36).slice(2, 10)}`;
  }
}

let activeCompany: string | null = null;

export async function startOfflineSync(companyId: string): Promise<void> {
  const baseUrl = getOfflineSyncBaseUrl();
  const apiKey = getOfflineSyncApiKey();
  if (!baseUrl || !apiKey) return;

  if (OfflineSync.isInitialized() && activeCompany === companyId) {
    void OfflineSync.syncNow();
    return;
  }
  activeCompany = companyId;

  const config: SyncConfig = {
    baseUrl,
    apiKey,
    deviceId: getDeviceId(),
    pdvCode: companyId, // routeKey: only this company's rows are returned
    platform: 'web',
    appVersion: process.env.REACT_APP_VERSION,
    streams: [...OFFLINESYNC_STREAMS],
    database: createLocalStorageDatabase(`company-${companyId}`),
    network: createHttpNetworkMonitor(`${baseUrl.replace(/\/$/, '')}/actuator/health/liveness`),
    bootstrapWithSnapshot: true,
    pageSize: 200,
  };

  await OfflineSync.init(config);
  try {
    await OfflineSync.registerDevice();
  } catch {
    // Offline at startup: the device registers on the next successful sync.
  }
  void OfflineSync.syncNow();
}

export function getCachedRows(stream: string): Promise<Record<string, unknown>[]> {
  return OfflineSync.query(stream);
}
